import { Timestamp } from 'firebase/firestore';
import type { MemberRole } from './organisation';

export const TEAM_UPDATE_POST_TYPES = [
  'update',
  'fyi',
  'clarify',
  'logistics',
  'question',
  'decision',
  'social',
  'request',
] as const;

export type TeamUpdatePostType = (typeof TEAM_UPDATE_POST_TYPES)[number];

export const TEAM_UPDATE_REQUEST_STATUSES = ['pending', 'accepted', 'declined'] as const;

export type TeamUpdateRequestStatus = (typeof TEAM_UPDATE_REQUEST_STATUSES)[number];

/** Org roles eligible for team-update group membership pickers. */
export const TEAM_UPDATE_MEMBER_ROLES: MemberRole[] = [
  'facilitator',
  'organisationAdmin',
  'moderator',
  'projectAdmin',
  'wanderWriter',
  'steward',
];

export const DEFAULT_TEAM_UPDATE_POLL_OPTIONS: Array<{ id: string; label: string }> = [
  { id: 'yes', label: 'Yes' },
  { id: 'no', label: 'No' },
  { id: 'maybe', label: 'Maybe / need to check' },
];

export const TEAM_UPDATE_MAX_IMAGES = 4;
export const TEAM_UPDATE_MAX_VOICE_MS = 2 * 60 * 1000;
export const TEAM_UPDATE_MIN_POLL_OPTIONS = 2;
export const TEAM_UPDATE_MAX_POLL_OPTIONS = 8;

export type TeamUpdateTranscriptStatus = 'pending' | 'ready' | 'failed';

export type TeamUpdateImageAttachment = {
  type: 'image';
  storagePath: string;
  downloadUrl: string;
};

export type TeamUpdateVoiceAttachment = {
  type: 'voice';
  storagePath: string;
  downloadUrl: string;
  durationMs: number;
  transcriptStatus: TeamUpdateTranscriptStatus;
  transcript?: string;
};

export type TeamUpdateAttachment = TeamUpdateImageAttachment | TeamUpdateVoiceAttachment;

export interface ITeamUpdateGroup {
  id: string;
  name: string;
  description?: string;
  /** Pinned “what’s true now” summary for the feed At a Glance strip. */
  atAGlance?: string;
  organisationId: string;
  memberIds: string[];
  memberRolesAllowed?: MemberRole[];
  createdBy: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export type TeamUpdatePollOption = {
  id: string;
  label: string;
};

export type ITeamUpdatePoll = {
  options: TeamUpdatePollOption[];
  /** Single-choice for v1; reserved for later. */
  allowMultiple?: boolean;
};

export interface ITeamUpdatePost {
  id: string;
  groupId: string;
  type: TeamUpdatePostType;
  title: string;
  body: string;
  authorId: string;
  authorName: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  replyCount: number;
  lastReplyAt?: Timestamp;
  poll?: ITeamUpdatePoll;
  attachments?: TeamUpdateAttachment[];
  /** Flat media fields used by the Expo app (dual-written by Admin). */
  imageUrls?: string[];
  imagePaths?: string[];
  audioUrl?: string;
  audioPath?: string;
  transcription?: string;
  transcriptionStatus?: TeamUpdateTranscriptStatus;
  editedAt?: Timestamp;
  /** Present when `type === 'request'` (one or more people). */
  requestAssignees?: TeamUpdateRequestAssignee[];
  /**
   * Legacy single-assignee fields (pre multi-ask). Prefer `requestAssignees`.
   * Still written for older clients / already-posted requests.
   */
  requestAssigneeId?: string;
  requestAssigneeName?: string;
  requestStatus?: TeamUpdateRequestStatus;
  requestRespondedAt?: Timestamp;
  requestTodoId?: string;
  /** Present when this request asks someone to take on a work-stream task. */
  workStreamId?: string;
  workStreamTitle?: string;
  contributionTaskId?: string;
}

export type TeamUpdateRequestAssignee = {
  userId: string;
  name: string;
};

/** Per-assignee accept/decline (doc id `${postId}_${authorId}`). */
export interface ITeamUpdateRequestResponse {
  id: string;
  groupId: string;
  postId: string;
  authorId: string;
  status: Exclude<TeamUpdateRequestStatus, 'pending'>;
  todoId?: string;
  createdAt: Timestamp;
  updatedAt?: Timestamp;
}

export interface ITeamUpdateReply {
  id: string;
  groupId: string;
  postId: string;
  authorId: string;
  authorName: string;
  content: string;
  createdAt: Timestamp;
  updatedAt?: Timestamp;
  attachments?: TeamUpdateAttachment[];
  imageUrls?: string[];
  imagePaths?: string[];
  audioUrl?: string;
  audioPath?: string;
  transcription?: string;
  transcriptionStatus?: TeamUpdateTranscriptStatus;
  editedAt?: Timestamp;
}
export interface ITeamUpdatePollVote {
  id: string;
  groupId: string;
  postId: string;
  optionId: string;
  authorId: string;
  createdAt: Timestamp;
}

/** Stable reaction keys used in Firestore doc ids (not the emoji glyph). */
export const TEAM_UPDATE_REACTION_IDS = [
  'thumbsup',
  'heart',
  'joy',
  'wow',
  'sad',
  'tada',
] as const;

export type TeamUpdateReactionId = (typeof TEAM_UPDATE_REACTION_IDS)[number];

export const TEAM_UPDATE_REACTION_EMOJI: Record<TeamUpdateReactionId, string> = {
  thumbsup: '👍',
  heart: '❤️',
  joy: '😂',
  wow: '😮',
  sad: '😢',
  tada: '🎉',
};

export interface ITeamUpdateReaction {
  id: string;
  groupId: string;
  postId: string;
  authorId: string;
  emoji: TeamUpdateReactionId;
  createdAt: Timestamp;
}

export type CreateTeamUpdateGroupInput = {
  name: string;
  description?: string;
  atAGlance?: string;
  organisationId: string;
  memberIds: string[];
  memberRolesAllowed?: MemberRole[];
  createdBy: string;
};

export type CreateTeamUpdatePollInput = {
  options: Array<{ id?: string; label: string }>;
  allowMultiple?: boolean;
};

export type CreateTeamUpdatePostInput = {
  groupId: string;
  type: TeamUpdatePostType;
  title: string;
  body: string;
  authorId: string;
  authorName: string;
  poll?: CreateTeamUpdatePollInput;
  attachments?: TeamUpdateAttachment[];
  /** Required when `type === 'request'` (at least one). */
  requestAssignees?: TeamUpdateRequestAssignee[];
  workStreamId?: string;
  workStreamTitle?: string;
  contributionTaskId?: string;
  /** Prefer allocating the id before uploads so Storage paths match the doc. */
  customId?: string;
};

export type CreateTeamUpdateReplyInput = {
  groupId: string;
  postId: string;
  authorId: string;
  authorName: string;
  content: string;
  attachments?: TeamUpdateAttachment[];
  customId?: string;
};

export type UpdateTeamUpdatePostInput = {
  title?: string;
  body?: string;
  type?: TeamUpdatePostType;
  attachments?: TeamUpdateAttachment[];
  requestAssignees?: TeamUpdateRequestAssignee[];
  /** Set to replace poll options; `null` removes the poll (e.g. type is no longer question). */
  poll?: CreateTeamUpdatePollInput | null;
};

export type UpdateTeamUpdateReplyInput = {
  content?: string;
  attachments?: TeamUpdateAttachment[];
};

/** Local drafts before upload (compose UI). */
export type TeamUpdateImageDraft = {
  key: string;
  file: File;
  previewUrl: string;
};

export type TeamUpdateVoiceDraft = {
  file: File;
  durationMs: number;
  previewUrl: string;
};

export type CreateTeamUpdatePostPayload = {
  type: TeamUpdatePostType;
  text: string;
  /** Optional headline for longer posts. */
  title?: string;
  poll?: CreateTeamUpdatePollInput;
  /** Required when posting a request (one or more people). */
  requestAssignees?: TeamUpdateRequestAssignee[];
  imageFiles?: File[];
  voice?: { file: File; durationMs: number };
};

/** Compose UI payload for a thread reply (text and/or media). */
export type CreateTeamUpdateReplyPayload = {
  content: string;
  imageFiles?: File[];
  voice?: { file: File; durationMs: number };
};

/** Active group call presence (mesh WebRTC room keyed off the team-update group). */
export type TeamUpdateCallStatus = 'live' | 'ended';

export interface ITeamUpdateCall {
  id: string;
  groupId: string;
  /** Firestore signalling sessionId, e.g. `${groupId}-call`. */
  callRoomId: string;
  status: TeamUpdateCallStatus;
  startedBy: string;
  startedByName: string;
  participantIds: string[];
  startedAt: Timestamp;
  updatedAt: Timestamp;
  endedAt?: Timestamp;
}

export function teamUpdateCallRoomId(groupId: string): string {
  return `${groupId}-call`;
}
